// src/pages/client/ClientCandidatesPage.jsx
// Client Candidate Upload - works with both URL params and subdomain routing
// 
// ENHANCED: Two upload modes
// 1. Single resume: Drag-and-drop with auto-fill (existing)
// 2. Bulk ZIP: Upload ZIP of resumes, review table, bulk send
//
// Access patterns:
// - invenire.app/client/supabase/candidates → tenantKey from URL param
// - supabase.invenire.app/admin/candidates → tenantKey from subdomain (prop)

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { trackPageView, trackCandidateInvited } from "../../utils/analytics";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.invenire.app";

export default function ClientCandidatesPage({ tenantKeyFromSubdomain }) {
  // Get tenantKey from URL params OR from subdomain prop
  const { tenantKey: tenantKeyFromUrl } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  
  // State
  const [tenant, setTenant] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upload mode: "single" or "bulk"
  const [uploadMode, setUploadMode] = useState("single");
  
  // =========================================
  // SINGLE MODE STATE
  // =========================================
  const [selectedJobId, setSelectedJobId] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [candidatePhone, setCandidatePhone] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Parsing state
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [wasParsed, setWasParsed] = useState(false);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);
  
  // Result state
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState(null);
  const [blueprintReady, setBlueprintReady] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // =========================================
  // BULK MODE STATE
  // =========================================
  // eslint-disable-next-line no-unused-vars
  const [zipFile, setZipFile] = useState(null);
  const [bulkCandidates, setBulkCandidates] = useState([]);
  const [bulkParsing, setBulkParsing] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [zipFileData, setZipFileData] = useState(null); // Store extracted file data for submission
  
  // Determine if we're on subdomain (affects link paths)
  const isSubdomain = Boolean(tenantKeyFromSubdomain);
  
  // Generate correct paths based on routing mode
  const getAdminPath = () => isSubdomain ? "/admin" : `/client/${tenantKey}`;
  
  // Apply tenant branding (memoized to avoid dependency issues)
  const branding = useMemo(() => tenant?.branding || {}, [tenant?.branding]);
  const primaryColor = branding.primaryColor || "#3ECF8E";
  const companyName = branding.companyName || tenant?.name || tenantKey;
  
  // Get backgroundColor from branding (matches ClientConfigPage approach)
  const backgroundColor = branding.backgroundColor || branding.background_color || '#0d1117';
  
  // Calculate if background is light or dark for text color
  const isLightBackground = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    }
    return false;
  }, [backgroundColor]);
  
  // Dynamic styles based on tenant branding - ALL styles here, no static styles
  const dynamicStyles = useMemo(() => {
    const textColor = branding.textColor || (isLightBackground ? '#111827' : '#ffffff');
    const textSecondary = isLightBackground ? '#4b5563' : '#9ca3af';
    const textMuted = isLightBackground ? '#6b7280' : '#6b7280';
    const cardBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.03)';
    const cardBorder = isLightBackground ? '#e5e7eb' : 'rgba(255,255,255,0.1)';
    const inputBg = isLightBackground ? '#f9fafb' : 'rgba(255,255,255,0.05)';
    const inputBorder = isLightBackground ? '#d1d5db' : 'rgba(255,255,255,0.1)';
    const buttonBg = branding.buttonPrimaryBg || primaryColor;
    const buttonText = branding.buttonTextColor || '#ffffff';
    const hoverBg = isLightBackground ? '#f3f4f6' : 'rgba(255,255,255,0.05)';
    const successBg = "rgba(34, 197, 94, 0.1)";
    const successBorder = "rgba(34, 197, 94, 0.3)";
    const errorBg = "rgba(239, 68, 68, 0.1)";
    const errorBorder = "rgba(239, 68, 68, 0.3)";
    
    return {
      // Core layout
      container: { minHeight: "100vh", backgroundColor: backgroundColor, color: textColor, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
      header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 40px", borderBottom: `1px solid ${cardBorder}` },
      title: { fontSize: 28, fontWeight: 700, margin: 0, color: primaryColor },
      subtitle: { fontSize: 14, color: textSecondary },
      backButton: { padding: "8px 16px", background: isLightBackground ? '#f3f4f6' : "rgba(255,255,255,0.1)", color: textColor, textDecoration: "none", borderRadius: 6, fontSize: 14, border: "none", cursor: "pointer" },
      content: { maxWidth: 1200, margin: "0 auto", padding: "40px 20px" },
      
      // Loading & Error states
      loading: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontSize: 18, color: textSecondary },
      errorTitle: { fontSize: 24, marginBottom: 10, color: textColor },
      errorText: { color: "#ef4444", marginBottom: 20 },
      backLink: { color: primaryColor, textDecoration: "none" },
      
      // Mode Toggle
      modeToggle: { display: "flex", gap: 12, marginBottom: 24 },
      modeButton: { padding: "12px 24px", background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8, color: textSecondary, fontSize: 14, fontWeight: 500, cursor: "pointer", transition: "all 0.2s" },
      modeButtonActive: { background: `${primaryColor}15`, borderColor: primaryColor, color: primaryColor },
      
      // Job Selection
      jobSection: { marginBottom: 24, maxWidth: 400 },
      
      // Two Column Layout
      twoColumn: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 },
      formSection: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      resultSection: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      
      // Section titles
      sectionTitle: { fontSize: 20, marginBottom: 8, marginTop: 0, color: textColor, fontWeight: 600 },
      sectionText: { color: textSecondary, fontSize: 14, marginBottom: 24 },
      
      // Form elements
      formGroup: { marginBottom: 20 },
      label: { display: "block", fontSize: 14, fontWeight: 500, color: textSecondary, marginBottom: 8 },
      input: { width: "100%", padding: "12px 16px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8, fontSize: 14, color: textColor, boxSizing: "border-box" },
      select: { width: "100%", padding: "12px 16px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8, fontSize: 14, color: textColor },
      hint: { fontSize: 12, color: textSecondary, marginTop: 6 },
      errorHint: { color: "#ef4444" },
      warningHint: { color: "#f59e0b" },
      
      // Buttons
      button: { padding: "12px 24px", background: buttonBg, color: buttonText, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
      submitBtn: { width: "100%", padding: "14px 24px", backgroundColor: buttonBg, color: buttonText, border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" },
      submitBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
      
      // Card base
      card: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      
      // Drop zone
      dropZone: { border: `2px dashed ${cardBorder}`, borderRadius: 12, padding: 32, textAlign: "center", cursor: "pointer", transition: "all 0.2s", background: cardBg },
      dropZoneActive: { borderColor: primaryColor, background: `${primaryColor}10` },
      dropIcon: { fontSize: 48, marginBottom: 12, color: textSecondary },
      dropText: { color: textColor, marginBottom: 4, fontWeight: 500 },
      dropHint: { fontSize: 13, color: textSecondary },
      fileSelected: { display: "flex", alignItems: "center", gap: 12, marginTop: 16, padding: 12, background: hoverBg, borderRadius: 8 },
      fileName: { flex: 1, color: textColor },
      removeFile: { background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 },
      
      // Bulk upload
      bulkSection: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      
      // Result/Status messages
      successMessage: { marginTop: 16, padding: 16, background: successBg, border: `1px solid ${successBorder}`, borderRadius: 8, color: "#22c55e" },
      errorMessage: { marginTop: 16, padding: 16, background: errorBg, border: `1px solid ${errorBorder}`, borderRadius: 8, color: "#ef4444" },
      
      // Results list
      resultsList: { marginTop: 24 },
      resultItem: { padding: 16, borderBottom: `1px solid ${cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center" },
      resultName: { fontWeight: 500, color: textColor },
      resultStatus: { fontSize: 12, padding: "4px 8px", borderRadius: 4 },
      resultSuccess: { background: successBg, color: "#22c55e" },
      resultError: { background: errorBg, color: "#ef4444" },
      
      // Empty state
      emptyState: { textAlign: "center", padding: 48, color: textSecondary },
      emptyIcon: { fontSize: 64, marginBottom: 16, color: textSecondary },
      
      // Table (for bulk results)
      table: { width: "100%", borderCollapse: "collapse", marginTop: 16 },
      th: { textAlign: "left", padding: "12px 16px", background: isLightBackground ? '#f9fafb' : 'rgba(255,255,255,0.05)', color: textSecondary, fontSize: 12, fontWeight: 600, textTransform: "uppercase", borderBottom: `1px solid ${cardBorder}` },
      td: { padding: "12px 16px", borderBottom: `1px solid ${cardBorder}`, fontSize: 14, color: textColor },
      
      // Expose values for inline use
      textColor, textSecondary, cardBg, cardBorder, inputBg, inputBorder, buttonBg, buttonText, isLightBackground, primaryColor, hoverBg, textMuted
    };
  }, [branding, primaryColor, backgroundColor, isLightBackground]);

  // Set document title
  useEffect(() => {
    const tenantName = tenant?.name || tenant?.branding?.companyName || tenantKey || '';
    document.title = tenantName ? `Invenire - ${tenantName} - Add Candidate` : 'Invenire - Add Candidate';
  }, [tenant, tenantKey]);

  // Track page view
  useEffect(() => {
    const pagePath = isSubdomain ? '/admin/candidates' : '/client/[key]/candidates';
    trackPageView(pagePath, 'Client Candidates');
  }, [isSubdomain]);
  
  // Load tenant and jobs
  useEffect(() => {
    if (!tenantKey) {
      setError("No client specified");
      setLoading(false);
      return;
    }
    
    async function loadData() {
      try {
        // Load tenant
        const tenantResponse = await fetch(`${API_BASE}/tenants/${tenantKey}`);
        if (!tenantResponse.ok) {
          throw new Error(`Client "${tenantKey}" not found`);
        }
        const tenantData = await tenantResponse.json();
        setTenant(tenantData);
        
        // Load jobs for this tenant
        const jobsResponse = await fetch(`${API_BASE}/tenants/${tenantKey}/jobs`);
        if (jobsResponse.ok) {
          const jobsData = await jobsResponse.json();
          setJobs(jobsData.jobs || []);
          
          // Auto-select first job if available
          if (jobsData.jobs && jobsData.jobs.length > 0) {
            setSelectedJobId(jobsData.jobs[0].id.toString());
          }
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [tenantKey]);
  
  // Poll for blueprint status when result is generating (single mode)
  useEffect(() => {
    if (!result || blueprintReady) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/profiles/status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ interview_id: result.interview_id })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.blueprint_ready) {
            setBlueprintReady(true);
            setTimeout(() => setEmailSent(true), 1500);
            clearInterval(pollInterval);
          }
        }
      } catch (err) {
        console.error("Blueprint poll error:", err);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [result, blueprintReady]);
  
  // Helper: Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  // =========================================
  // SINGLE MODE HANDLERS
  // =========================================
  
  // Parse resume to extract contact info
  const parseResume = useCallback(async (file) => {
    setIsParsing(true);
    setParseError(null);
    
    try {
      const fileBase64 = await fileToBase64(file);
      
      const response = await fetch(`${API_BASE}/profiles/parse-resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          file_name: file.name,
          file_base64: fileBase64
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to parse resume");
      }
      
      const data = await response.json();
      
      if (data.success) {
        if (data.name) setCandidateName(data.name);
        if (data.email) setCandidateEmail(data.email);
        if (data.phone) setCandidatePhone(data.phone);
        setWasParsed(true);
      } else {
        setParseError(data.error || "Could not extract contact info from resume");
      }
      
    } catch (err) {
      console.error("Resume parse error:", err);
      setParseError("Failed to parse resume. You can still enter details manually.");
    } finally {
      setIsParsing(false);
    }
  }, []);
  
  // Handle file selection (from input or drop)
  const handleFile = useCallback((file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = [
      "application/pdf", 
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];
    const isValidType = validTypes.includes(file.type) || 
                        file.name.endsWith(".pdf") || 
                        file.name.endsWith(".docx");
    
    if (!isValidType) {
      setResultError("Please upload a PDF or DOCX file");
      return;
    }
    
    setResumeFile(file);
    setResultError(null);
    setParseError(null);
    setWasParsed(false);
    
    setCandidateName("");
    setCandidateEmail("");
    setCandidatePhone("");
    
    parseResume(file);
  }, [parseResume]);
  
  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };
  
  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);
  
  // Handle single form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedJobId) {
      setResultError("Please select a job");
      return;
    }
    if (!candidateName.trim()) {
      setResultError("Please enter candidate name");
      return;
    }
    if (!resumeFile) {
      setResultError("Please upload a resume");
      return;
    }
    
    setSubmitting(true);
    setResult(null);
    setResultError(null);
    
    try {
      const fileBase64 = await fileToBase64(resumeFile);
      
      const response = await fetch(`${API_BASE}/profiles/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_key: tenantKey,
          job_id: parseInt(selectedJobId),
          candidate_name: candidateName.trim(),
          candidate_email: candidateEmail.trim() || undefined,
          file_name: resumeFile.name,
          file_base64: fileBase64
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to create interview");
      }
      
      const data = await response.json();
      setResult(data);
      setBlueprintReady(false);
      setEmailSent(false);
      
      trackCandidateInvited(tenantKey, data.interview_id);
      
      // Reset form
      setCandidateName("");
      setCandidateEmail("");
      setCandidatePhone("");
      setResumeFile(null);
      setWasParsed(false);
      
    } catch (err) {
      setResultError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // =========================================
  // BULK MODE HANDLERS
  // =========================================
  
  // Handle ZIP file selection
  const handleZipFile = useCallback(async (file) => {
    if (!file) return;
    
    if (!file.name.endsWith(".zip")) {
      setResultError("Please upload a ZIP file");
      return;
    }
    
    setZipFile(file);
    setBulkCandidates([]);
    setBulkResults(null);
    setResultError(null);
    setBulkParsing(true);
    
    try {
      const fileBase64 = await fileToBase64(file);
      setZipFileData(fileBase64); // Store for submission
      
      const response = await fetch(`${API_BASE}/profiles/parse-resume-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          file_name: file.name,
          file_base64: fileBase64
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to parse ZIP file");
      }
      
      const data = await response.json();
      
      // Convert to editable state with selection
      const editableCandidates = data.candidates.map((c, idx) => ({
        ...c,
        id: idx,
        selected: c.success && c.name && c.email, // Auto-select if has name AND email
      }));
      
      setBulkCandidates(editableCandidates);
      
    } catch (err) {
      console.error("ZIP parse error:", err);
      setResultError(err.message);
    } finally {
      setBulkParsing(false);
    }
  }, []);
  
  // Handle ZIP drag/drop
  const handleZipDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleZipFile(files[0]);
    }
  }, [handleZipFile]);
  
  // Handle ZIP file input change
  const handleZipFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleZipFile(file);
  };
  
  // Update candidate field
  const updateCandidate = (id, field, value) => {
    setBulkCandidates(prev => prev.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };
  
  // Toggle candidate selection
  const toggleCandidate = (id) => {
    setBulkCandidates(prev => prev.map(c => 
      c.id === id ? { ...c, selected: !c.selected } : c
    ));
  };
  
  // Select/deselect all
  const toggleAllCandidates = () => {
    const allSelected = bulkCandidates.every(c => c.selected || !c.success);
    setBulkCandidates(prev => prev.map(c => ({
      ...c,
      selected: c.success ? !allSelected : false
    })));
  };
  
  // Submit bulk candidates
  const handleBulkSubmit = async () => {
    if (!selectedJobId) {
      setResultError("Please select a job");
      return;
    }
    
    const selectedCandidates = bulkCandidates.filter(c => c.selected && c.success);
    if (selectedCandidates.length === 0) {
      setResultError("Please select at least one candidate");
      return;
    }
    
    setBulkSubmitting(true);
    setResultError(null);
    
    try {
      // Re-read ZIP and extract files for submission
      // We need to send the file data for each candidate
      const JSZip = await import('jszip');
      const zip = await JSZip.default.loadAsync(
        Uint8Array.from(atob(zipFileData), c => c.charCodeAt(0))
      );
      
      // Build candidates array with file data
      const candidatesWithFiles = await Promise.all(
        selectedCandidates.map(async (c) => {
          // Find the file in the ZIP
          const fileEntry = Object.keys(zip.files).find(f => 
            f.endsWith(c.file_name) || f.split('/').pop() === c.file_name
          );
          
          let fileBase64 = "";
          if (fileEntry) {
            const fileData = await zip.files[fileEntry].async("base64");
            fileBase64 = fileData;
          }
          
          return {
            name: c.name,
            email: c.email,
            file_name: c.file_name,
            file_base64: fileBase64
          };
        })
      );
      
      const response = await fetch(`${API_BASE}/profiles/create-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_key: tenantKey,
          job_id: parseInt(selectedJobId),
          candidates: candidatesWithFiles
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to create interviews");
      }
      
      const data = await response.json();
      setBulkResults(data);
      
      // Track each successful candidate
      data.results.filter(r => r.success).forEach(r => {
        trackCandidateInvited(tenantKey, r.interview_id);
      });
      
    } catch (err) {
      console.error("Bulk submit error:", err);
      setResultError(err.message);
    } finally {
      setBulkSubmitting(false);
    }
  };
  
  // Reset bulk mode
  const resetBulkMode = () => {
    setZipFile(null);
    setBulkCandidates([]);
    setBulkResults(null);
    setZipFileData(null);
  };
  
  // =========================================
  // RENDER HELPERS
  // =========================================
  
  // eslint-disable-next-line no-unused-vars
  const getSelectedJob = () => jobs.find(j => j.id.toString() === selectedJobId);
  const selectedCount = bulkCandidates.filter(c => c.selected).length;
  
  // Loading state
  if (loading) {
    return <div style={dynamicStyles.loading}>Loading...</div>;
  }
  
  // Error state
  if (error) {
    return (
      <div style={dynamicStyles.container}>
        <div style={dynamicStyles.content}>
          <h2 style={{ ...dynamicStyles.errorTitle, color: dynamicStyles.textColor }}>Error</h2>
          <p style={dynamicStyles.errorText}>{error}</p>
          <Link to="/" style={{ ...dynamicStyles.backLink, color: primaryColor }}>← Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={dynamicStyles.container}>
      {/* Header */}
      <header style={dynamicStyles.header}>
        <div>
          <h1 style={dynamicStyles.title}>{companyName}</h1>
          <span style={dynamicStyles.subtitle}>Add Candidates</span>
        </div>
        <Link to={getAdminPath()} style={dynamicStyles.backButton}>← Back to Dashboard</Link>
      </header>
      
      {/* Content */}
      <div style={dynamicStyles.content}>
        {/* Mode Toggle */}
        <div style={dynamicStyles.modeToggle}>
          <button
            style={{
              ...dynamicStyles.modeButton,
              background: dynamicStyles.isLightBackground ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${dynamicStyles.cardBorder}`,
              color: dynamicStyles.textSecondary,
              ...(uploadMode === "single" ? {
                background: `${primaryColor}1a`,
                borderColor: primaryColor,
                color: primaryColor
              } : {})
            }}
            onClick={() => setUploadMode("single")}
          >
            📄 Single Resume
          </button>
          <button
            style={{
              ...dynamicStyles.modeButton,
              background: dynamicStyles.isLightBackground ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${dynamicStyles.cardBorder}`,
              color: dynamicStyles.textSecondary,
              ...(uploadMode === "bulk" ? {
                background: `${primaryColor}1a`,
                borderColor: primaryColor,
                color: primaryColor
              } : {})
            }}
            onClick={() => setUploadMode("bulk")}
          >
            📦 Bulk Upload (ZIP)
          </button>
        </div>
        
        {/* Job Selection - Shared */}
        <div style={dynamicStyles.jobSection}>
          <label style={dynamicStyles.label}>Select Job</label>
          {jobs.length > 0 ? (
            <select
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              style={dynamicStyles.select}
            >
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.title}</option>
              ))}
            </select>
          ) : (
            <p style={dynamicStyles.noJobs}>No jobs found for this client.</p>
          )}
        </div>
        
        {/* SINGLE MODE */}
        {uploadMode === "single" && (
          <div style={dynamicStyles.twoColumn}>
            {/* Left: Form */}
            <div style={dynamicStyles.formSection}>
              <h2 style={dynamicStyles.sectionTitle}>Upload Resume</h2>
              <p style={dynamicStyles.sectionText}>
                Drag and drop a resume to auto-fill candidate details
              </p>
              
              <form onSubmit={handleSubmit}>
                {/* Drop Zone */}
                <div style={dynamicStyles.formGroup}>
                  <label style={dynamicStyles.label}>Resume (PDF or DOCX)</label>
                  <div
                    ref={dropZoneRef}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('resume-input').click()}
                    style={{
                      ...dynamicStyles.dropZone,
                      borderColor: isDragging ? primaryColor : "rgba(255,255,255,0.2)",
                      background: isDragging ? `${primaryColor}1a` : "transparent"
                    }}
                  >
                    <input
                      id="resume-input"
                      type="file"
                      accept=".pdf,.docx"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    
                    {isParsing ? (
                      <div style={dynamicStyles.dropZoneContent}>
                        <div style={dynamicStyles.spinner}></div>
                        <p style={dynamicStyles.dropText}>Parsing resume...</p>
                      </div>
                    ) : resumeFile ? (
                      <div style={dynamicStyles.dropZoneContent}>
                        <span style={dynamicStyles.fileIcon}>📄</span>
                        <p style={dynamicStyles.fileName}>{resumeFile.name}</p>
                        {wasParsed && (
                          <p style={{ ...dynamicStyles.parseSuccess, color: primaryColor }}>
                            ✓ Contact info extracted
                          </p>
                        )}
                        {parseError && (
                          <p style={dynamicStyles.parseWarning}>⚠️ {parseError}</p>
                        )}
                      </div>
                    ) : (
                      <div style={dynamicStyles.dropZoneContent}>
                        <span style={dynamicStyles.uploadIcon}>📥</span>
                        <p style={dynamicStyles.dropText}>
                          {isDragging ? "Drop resume here" : "Drag resume here or click to browse"}
                        </p>
                        <p style={dynamicStyles.dropHint}>PDF or DOCX</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Name */}
                <div style={dynamicStyles.formGroup}>
                  <label style={dynamicStyles.label}>
                    Candidate Name
                    {wasParsed && candidateName && (
                      <span style={{ ...dynamicStyles.autoFilled, color: primaryColor, marginLeft: 8 }}>
                        (auto-filled)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="e.g. John Smith"
                    style={dynamicStyles.input}
                    required
                  />
                </div>
                
                {/* Email */}
                <div style={dynamicStyles.formGroup}>
                  <label style={dynamicStyles.label}>
                    Candidate Email
                    {wasParsed && candidateEmail && (
                      <span style={{ ...dynamicStyles.autoFilled, color: primaryColor, marginLeft: 8 }}>
                        (auto-filled)
                      </span>
                    )}
                  </label>
                  <input
                    type="email"
                    value={candidateEmail}
                    onChange={(e) => setCandidateEmail(e.target.value)}
                    placeholder="e.g. john@example.com"
                    style={dynamicStyles.input}
                  />
                </div>
                
                {/* Phone (only show if parsed) */}
                {candidatePhone && (
                  <div style={dynamicStyles.formGroup}>
                    <label style={dynamicStyles.label}>
                      Phone
                      <span style={{ ...dynamicStyles.autoFilled, color: primaryColor, marginLeft: 8 }}>
                        (auto-filled)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={candidatePhone}
                      onChange={(e) => setCandidatePhone(e.target.value)}
                      style={dynamicStyles.input}
                    />
                  </div>
                )}
                
                {/* Error */}
                {resultError && (
                  <div style={dynamicStyles.errorBox}>{resultError}</div>
                )}
                
                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting || jobs.length === 0}
                  style={{
                    width: "100%",
                    padding: "14px 24px",
                    backgroundColor: primaryColor,
                    color: "#ffffff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: (submitting || jobs.length === 0) ? "not-allowed" : "pointer",
                    opacity: (submitting || jobs.length === 0) ? 0.6 : 1
                  }}
                >
                  {submitting ? "Creating Interview..." : "Send Interview Invitation"}
                </button>
              </form>
            </div>
            
            {/* Right: Result */}
            <div style={dynamicStyles.resultSection}>
              <h2 style={dynamicStyles.sectionTitle}>Interview Status</h2>
              
              {!result ? (
                <div style={dynamicStyles.resultPlaceholder}>
                  <div style={dynamicStyles.placeholderIcon}>📋</div>
                  <p>Upload a resume and fill in details to send an interview invitation.</p>
                  <p style={dynamicStyles.placeholderHint}>
                    The candidate will receive an email with their personalized interview link.
                  </p>
                </div>
              ) : (
                <div style={dynamicStyles.resultCard}>
                  <div style={dynamicStyles.successIcon}>✓</div>
                  <h3 style={dynamicStyles.resultTitle}>Interview Created!</h3>
                  
                  <div style={dynamicStyles.resultField}>
                    <span style={dynamicStyles.resultLabel}>Status</span>
                    <span style={dynamicStyles.resultValue}>
                      {emailSent ? "✅ Email Sent" : blueprintReady ? "📧 Sending Email..." : "⏳ Generating Questions..."}
                    </span>
                  </div>
                  
                  <div style={dynamicStyles.resultField}>
                    <span style={dynamicStyles.resultLabel}>Interview ID</span>
                    <span style={dynamicStyles.resultValue}>{result.interview_id}</span>
                  </div>
                  
                  <div style={dynamicStyles.urlSection}>
                    <label style={dynamicStyles.label}>Interview Link</label>
                    <input
                      type="text"
                      value={result.url}
                      readOnly
                      style={dynamicStyles.urlInput}
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(result.url)}
                      style={dynamicStyles.copyButton}
                    >
                      Copy Link
                    </button>
                  </div>
                  
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...dynamicStyles.openButton,
                      background: blueprintReady ? primaryColor : "#6b7280",
                      pointerEvents: blueprintReady ? "auto" : "none"
                    }}
                  >
                    {blueprintReady ? "Open Interview →" : "Waiting for Blueprint..."}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* BULK MODE */}
        {uploadMode === "bulk" && (
          <div style={dynamicStyles.bulkContainer}>
            {/* Bulk Results View */}
            {bulkResults ? (
              <div style={dynamicStyles.bulkResults}>
                <div style={dynamicStyles.bulkResultsHeader}>
                  <h2 style={dynamicStyles.sectionTitle}>
                    ✅ {bulkResults.successful} of {bulkResults.total} Invitations Sent
                  </h2>
                  <button onClick={resetBulkMode} style={dynamicStyles.resetButton}>
                    Upload New ZIP
                  </button>
                </div>
                
                <div style={dynamicStyles.bulkResultsTable}>
                  <table style={dynamicStyles.table}>
                    <thead>
                      <tr>
                        <th style={dynamicStyles.th}>Candidate</th>
                        <th style={dynamicStyles.th}>Email</th>
                        <th style={dynamicStyles.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResults.results.map((r, idx) => (
                        <tr key={idx} style={r.success ? {} : { background: 'rgba(239, 68, 68, 0.05)' }}>
                          <td style={dynamicStyles.td}>{r.candidate_name}</td>
                          <td style={dynamicStyles.td}>{r.file_name}</td>
                          <td style={dynamicStyles.td}>
                            {r.success ? (
                              <span style={{ color: primaryColor }}>✓ Sent</span>
                            ) : (
                              <span style={{ color: "#ef4444" }}>✗ {r.error}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : bulkCandidates.length > 0 ? (
              /* Review Table */
              <div style={dynamicStyles.bulkReview}>
                <div style={dynamicStyles.bulkReviewHeader}>
                  <h2 style={dynamicStyles.sectionTitle}>
                    Review Candidates ({selectedCount} selected)
                  </h2>
                  <div style={dynamicStyles.bulkActions}>
                    <button onClick={toggleAllCandidates} style={dynamicStyles.selectAllButton}>
                      {bulkCandidates.every(c => c.selected || !c.success) ? "Deselect All" : "Select All"}
                    </button>
                    <button onClick={resetBulkMode} style={dynamicStyles.resetButton}>
                      Cancel
                    </button>
                  </div>
                </div>
                
                <div style={dynamicStyles.bulkTableContainer}>
                  <table style={dynamicStyles.table}>
                    <thead>
                      <tr>
                        <th style={{ ...dynamicStyles.th, width: 40 }}>
                          <input
                            type="checkbox"
                            checked={bulkCandidates.every(c => c.selected || !c.success)}
                            onChange={toggleAllCandidates}
                          />
                        </th>
                        <th style={dynamicStyles.th}>File</th>
                        <th style={dynamicStyles.th}>Name</th>
                        <th style={dynamicStyles.th}>Email</th>
                        <th style={dynamicStyles.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkCandidates.map((c) => (
                        <tr 
                          key={c.id} 
                          style={{
                            ...dynamicStyles.tableRow,
                            opacity: c.success ? 1 : 0.5,
                            background: c.selected ? `${primaryColor}1a` : "transparent"
                          }}
                        >
                          <td style={dynamicStyles.td}>
                            <input
                              type="checkbox"
                              checked={c.selected}
                              disabled={!c.success}
                              onChange={() => toggleCandidate(c.id)}
                            />
                          </td>
                          <td style={dynamicStyles.td}>
                            <span style={dynamicStyles.fileName}>{c.file_name}</span>
                          </td>
                          <td style={dynamicStyles.td}>
                            {c.success ? (
                              <input
                                type="text"
                                value={c.name || ""}
                                onChange={(e) => updateCandidate(c.id, "name", e.target.value)}
                                style={dynamicStyles.tableInput}
                                placeholder="Name"
                              />
                            ) : (
                              <span style={{ color: "#6b7280" }}>—</span>
                            )}
                          </td>
                          <td style={dynamicStyles.td}>
                            {c.success ? (
                              <input
                                type="email"
                                value={c.email || ""}
                                onChange={(e) => updateCandidate(c.id, "email", e.target.value)}
                                style={dynamicStyles.tableInput}
                                placeholder="Email"
                              />
                            ) : (
                              <span style={{ color: "#6b7280" }}>—</span>
                            )}
                          </td>
                          <td style={dynamicStyles.td}>
                            {c.success ? (
                              c.name && c.email ? (
                                <span style={{ color: primaryColor }}>✓ Ready</span>
                              ) : (
                                <span style={{ color: "#fbbf24" }}>⚠ Missing info</span>
                              )
                            ) : (
                              <span style={{ color: "#ef4444" }}>✗ {c.error}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Error */}
                {resultError && (
                  <div style={dynamicStyles.errorBox}>{resultError}</div>
                )}
                
                {/* Submit */}
                <div style={dynamicStyles.bulkSubmitSection}>
                  <button
                    onClick={handleBulkSubmit}
                    disabled={bulkSubmitting || selectedCount === 0}
                    style={{
                      ...dynamicStyles.submitBtn,
                      maxWidth: 400,
                      background: bulkSubmitting || selectedCount === 0 ? "#6b7280" : primaryColor,
                      cursor: bulkSubmitting || selectedCount === 0 ? "not-allowed" : "pointer"
                    }}
                  >
                    {bulkSubmitting 
                      ? "Sending Invitations..." 
                      : `Send ${selectedCount} Interview Invitation${selectedCount !== 1 ? 's' : ''}`
                    }
                  </button>
                </div>
              </div>
            ) : (
              /* ZIP Upload */
              <div style={dynamicStyles.bulkUpload}>
                <h2 style={dynamicStyles.sectionTitle}>Bulk Upload</h2>
                <p style={dynamicStyles.sectionText}>
                  Upload a ZIP file containing multiple resumes (PDF or DOCX).
                  Each resume will be parsed for contact information.
                </p>
                
                <div
                  ref={dropZoneRef}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleZipDrop}
                  onClick={() => document.getElementById('zip-input').click()}
                  style={{
                    ...dynamicStyles.dropZone,
                    ...dynamicStyles.dropZoneLarge,
                    borderColor: isDragging ? primaryColor : "rgba(255,255,255,0.2)",
                    background: isDragging ? `${primaryColor}1a` : "transparent"
                  }}
                >
                  <input
                    id="zip-input"
                    type="file"
                    accept=".zip"
                    onChange={handleZipFileChange}
                    style={{ display: "none" }}
                  />
                  
                  {bulkParsing ? (
                    <div style={dynamicStyles.dropZoneContent}>
                      <div style={dynamicStyles.spinnerLarge}></div>
                      <p style={dynamicStyles.dropText}>Parsing resumes...</p>
                      <p style={dynamicStyles.dropHint}>This may take a moment</p>
                    </div>
                  ) : (
                    <div style={dynamicStyles.dropZoneContent}>
                      <span style={{ fontSize: 48, marginBottom: 16 }}>📦</span>
                      <p style={dynamicStyles.dropText}>
                        {isDragging ? "Drop ZIP file here" : "Drag ZIP file here or click to browse"}
                      </p>
                      <p style={dynamicStyles.dropHint}>
                        Contains PDF or DOCX resumes
                      </p>
                    </div>
                  )}
                </div>
                
                {resultError && (
                  <div style={{ ...dynamicStyles.errorBox, marginTop: 20 }}>{resultError}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

